import { useState } from 'react';
import { api, describeError } from '../../api.ts';
import { Link } from '../../router.tsx';
import { announceChange } from '../../ui/changes.ts';
import { useKeys } from '../../ui/keys.ts';
import { Kbd } from '../../ui/kit.tsx';
import { useToast } from '../../ui/toasts.tsx';
import './intake.css';

/**
 * Add coupons (docs/coupon-intake.md): paste one or more outlines, preview what every site will get, create them in
 * one step. Each result says what really happened: store, who wrote each field, per-site title and link, live or why not.
 */

interface SiteResult {
  ref: string;
  name: string;
  title: string | null;
  description: string | null;
  textOrigin: string | null;
  link: { status: string; url: string | null; message: string | null } | null;
  state: 'approved' | 'pending_review' | 'already_listed' | 'not_written';
  why: string[];
  warnings: string[];
}

interface Result {
  text: string;
  status: 'created' | 'updated' | 'duplicate' | 'needs_merchant' | 'invalid';
  message: string;
  coupon: { ref: string; title: string } | null;
  merchant: { ref: string; name: string; slug: string } | null;
  countryCode: string | null;
  fields: { field: string; value: string; origin: string }[];
  sites: SiteResult[];
  problems: string[];
}

const EXAMPLE = `https://www.nike.com/gb/sale
Country: GB
Code: EXTRA20
Discount: 20%
Min spend: 50
End date: 2026-11-30

---
Shop: nike
Country: GB
Title: <Free delivery on everything this weekend>
End date: 2026-11-08`;

const FIELD_LABELS: Record<string, string> = {
  kind: 'Type',
  code: 'Code',
  discount: 'Discount',
  title: 'Title',
  minPurchase: 'Minimum spend',
  conditions: 'Conditions',
  newCustomersOnly: 'Audience',
  startsAt: 'Starts',
  endsAt: 'Ends',
  url: 'Landing page',
};

const ORIGIN_LABELS: Record<string, string> = { human: 'you typed', source: 'from the source', derived: 'worked out', ai: 'AI', rules: 'copy rules' };

const STATUS_TONE: Record<Result['status'], string> = { created: 'badge-ok', updated: 'badge-ok', duplicate: '', needs_merchant: 'badge-warn', invalid: 'badge-warn' };
const STATUS_LABEL: Record<Result['status'], string> = { created: 'new', updated: 'updated', duplicate: 'already listed', needs_merchant: 'store needed', invalid: 'not usable' };

const STATE_LABEL: Record<SiteResult['state'], string> = { approved: 'live', pending_review: 'waiting for review', already_listed: 'already on this site', not_written: 'no title' };

export function AddCoupons() {
  const toast = useToast();
  const [text, setText] = useState('');
  const [country, setCountry] = useState('GB');
  const [results, setResults] = useState<Result[] | null>(null);
  const [mode, setMode] = useState<'preview' | 'created' | null>(null);
  const [busy, setBusy] = useState(false);

  const send = async (submit: boolean) => {
    if (!text.trim() || busy) return;
    setBusy(true);
    try {
      const out = await api.op<{ results: Result[] }>(submit ? 'admin.intake.submit' : 'admin.intake.preview', { text, countryCode: country || undefined });
      setResults(out.results);
      setMode(submit ? 'created' : 'preview');
      if (submit) {
        announceChange();
        const made = out.results.filter((r) => r.status === 'created' || r.status === 'updated').length;
        toast(made ? 'success' : 'info', `${made} of ${out.results.length} saved`);
        if (made === out.results.length) setText('');
      }
    } catch (error) {
      toast('error', describeError(error));
    }
    setBusy(false);
  };
  useKeys({ escape: () => setResults(null) });

  return (
    <section className="page intake">
      <header className="page-head">
        <div>
          <h1>Add coupons</h1>
          <p className="muted">
            Paste one or more coupons, one block each (blank line or <code>---</code> between them). Each becomes a coupon on every site of yours that shows
            the store, with its own title and description per site and its tracking link. Write a value in <code>&lt;…&gt;</code> to use it word for word on
            every site.
          </p>
        </div>
      </header>

      <div className="intake-input">
        <label className="intake-text">
          Coupons
          <textarea
            rows={12}
            spellCheck={false}
            value={text}
            placeholder={EXAMPLE}
            onChange={(e) => {
              setText(e.target.value);
              setMode(null);
            }}
            onKeyDown={(e) => {
              if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
                e.preventDefault();
                void send(mode === 'preview');
              }
            }}
          />
        </label>
        <aside className="intake-help small muted">
          <p>
            <strong>Fields</strong> (any order, "Field: value"): Title, Code, Discount (20%, £10, free delivery), Min spend, Conditions, New customers (yes),
            Start date, End date (2026-11-30 or 30.11.2026), Shop, Country, Site, Description. A link on its own line is the landing page.
          </p>
          <p>Without "Shop", the store is found from the link. Without an end date a coupon runs 30 days.</p>
          <label className="inline-field">
            Default country
            <input maxLength={2} value={country} onChange={(e) => setCountry(e.target.value.toUpperCase())} style={{ width: '4em' }} />
          </label>
        </aside>
      </div>
      <div className="form-actions">
        <button type="button" className="btn" disabled={busy || !text.trim()} onClick={() => void send(false)}>
          {busy && mode !== 'preview' ? 'Checking…' : 'Preview'} {mode === 'preview' ? null : <Kbd>⌘ Enter</Kbd>}
        </button>
        <button type="button" className="btn btn-primary" disabled={busy || !text.trim()} onClick={() => void send(true)}>
          {busy ? 'Working…' : 'Create coupons'} {mode === 'preview' ? <Kbd>⌘ Enter</Kbd> : null}
        </button>
      </div>

      {results ? (
        <div className="intake-results" aria-live="polite">
          <h2 className="section-title">{mode === 'preview' ? 'Preview: nothing saved yet' : 'Result'}</h2>
          {results.map((result, index) => (
            <article key={index} className="intake-card">
              <header>
                <span className={`badge ${STATUS_TONE[result.status]}`}>{STATUS_LABEL[result.status]}</span>
                <strong>{result.merchant ? `${result.merchant.name} · ${result.countryCode}` : 'No store'}</strong>
                {result.coupon && mode === 'created' ? <Link to={`/coupons?coupon=${result.coupon.ref}`}>{result.coupon.ref}</Link> : null}
              </header>
              <p>{result.message}</p>
              {result.problems.length ? (
                <ul className="small text-warn">
                  {result.problems.map((problem) => (
                    <li key={problem}>{problem}</li>
                  ))}
                </ul>
              ) : null}
              {result.fields.length ? (
                <dl className="intake-fields small">
                  {result.fields.map((field) => (
                    <div key={field.field}>
                      <dt>{FIELD_LABELS[field.field] ?? field.field}</dt>
                      <dd>
                        {field.value} <span className={`origin origin-${field.origin}`}>{ORIGIN_LABELS[field.origin] ?? field.origin}</span>
                      </dd>
                    </div>
                  ))}
                </dl>
              ) : null}
              {result.sites.length ? (
                <table className="table" aria-label="Per site">
                  <thead>
                    <tr>
                      <th scope="col">Site</th>
                      <th scope="col">Title and description</th>
                      <th scope="col">Link</th>
                      <th scope="col">State</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.sites.map((site) => (
                      <tr key={site.ref}>
                        <td>{site.name}</td>
                        <td>
                          {site.title ? (
                            <>
                              <strong>{site.title}</strong> <span className={`origin origin-${site.textOrigin}`}>{ORIGIN_LABELS[site.textOrigin ?? ''] ?? site.textOrigin}</span>
                              <div className="small muted">{site.description}</div>
                            </>
                          ) : (
                            <span className="muted">—</span>
                          )}
                          {site.warnings.length ? <div className="small text-warn">{site.warnings.join(' · ')}</div> : null}
                        </td>
                        <td className="small">{site.link ? (site.link.status === 'ok' ? 'ok' : <span className="text-warn">{site.link.message ?? site.link.status}</span>) : '—'}</td>
                        <td className="small">
                          <span className={`badge ${site.state === 'approved' ? 'badge-ok' : ''}`}>{STATE_LABEL[site.state]}</span>
                          {site.why.length ? <div className="text-warn">{site.why.join(' · ')}</div> : null}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : null}
            </article>
          ))}
        </div>
      ) : null}
    </section>
  );
}
